import React, { useState, useMemo, useEffect } from 'react';
import { Invoice } from '../types';
import { apiService } from '../services/apiService';
import { 
  CurrencyEuroIcon, 
  ChartBarIcon,
  CalendarIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon
} from '@heroicons/react/24/outline';

interface QuarterlyTaxStatsProps {
  invoices: Invoice[];
}

interface QuarterlyData {
  quarter: string;
  totalRevenue: number;
  totalIVA: number;
  totalIRPF: number;
  invoiceCount: number;
}

export const QuarterlyTaxStats: React.FC<QuarterlyTaxStatsProps> = ({ invoices }) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [apiQuarters, setApiQuarters] = useState<Record<number, { ingresos_base?: number; iva_repercutido?: number; irpf_retenido?: number; pendientes_revision?: number }>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const results: Record<number, any> = {};
      for (let q = 1; q <= 4; q++) {
        const res = await apiService.getFiscalQuarterlySummary(selectedYear, q);
        if (res.data) results[q] = res.data;
      }
      if (!cancelled) setApiQuarters(results);
    })();
    return () => { cancelled = true; };
  }, [selectedYear]);

  const quarterlyData = useMemo(() => {
    const currentYear = selectedYear;
    const quarters: QuarterlyData[] = [
      { quarter: 'Q1', totalRevenue: 0, totalIVA: 0, totalIRPF: 0, invoiceCount: 0 },
      { quarter: 'Q2', totalRevenue: 0, totalIVA: 0, totalIRPF: 0, invoiceCount: 0 },
      { quarter: 'Q3', totalRevenue: 0, totalIVA: 0, totalIRPF: 0, invoiceCount: 0 },
      { quarter: 'Q4', totalRevenue: 0, totalIVA: 0, totalIRPF: 0, invoiceCount: 0 }
    ];

    invoices.forEach(invoice => {
      const invoiceYear = new Date(invoice.date).getFullYear();
      if (invoiceYear === currentYear) {
        const month = new Date(invoice.date).getMonth();
        const quarterIndex = Math.floor(month / 3);
        
        quarters[quarterIndex].totalRevenue += invoice.total;
        quarters[quarterIndex].totalIVA += invoice.ivaAmount;
        quarters[quarterIndex].totalIRPF += invoice.irpfAmount;
        quarters[quarterIndex].invoiceCount += 1;
      }
    });

    return quarters;
  }, [invoices, selectedYear]);

  const mergedQuarters = quarterlyData.map((q, i) => {
    const api = apiQuarters[i + 1];
    if (!api) return q;
    return {
      ...q,
      totalRevenue: api.ingresos_base ?? q.totalRevenue,
      totalIVA: api.iva_repercutido ?? q.totalIVA,
      totalIRPF: api.irpf_retenido ?? q.totalIRPF,
    };
  });
  const totalYearRevenue = mergedQuarters.reduce((sum, q) => sum + q.totalRevenue, 0);
  const totalYearIVA = mergedQuarters.reduce((sum, q) => sum + q.totalIVA, 0);
  const totalYearIRPF = mergedQuarters.reduce((sum, q) => sum + q.totalIRPF, 0);
  const totalYearInvoices = mergedQuarters.reduce((sum, q) => sum + q.invoiceCount, 0);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(amount);
  };

  const getQuarterName = (quarter: string) => {
    const quarterNames = {
      'Q1': 'Enero - Marzo',
      'Q2': 'Abril - Junio',
      'Q3': 'Julio - Septiembre',
      'Q4': 'Octubre - Diciembre'
    };
    return quarterNames[quarter as keyof typeof quarterNames];
  };

  const getBarHeight = (value: number, maxValue: number) => {
    if (maxValue === 0) return 0;
    return Math.max((value / maxValue) * 100, 5); // Mínimo 5% de altura
  };

  const maxRevenue = Math.max(...mergedQuarters.map(q => q.totalRevenue));

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <ChartBarIcon className="h-8 w-8 text-blue-600" />
          <div>
            <h3 className="text-xl font-bold text-gray-900">Estadísticas Trimestrales</h3>
            <p className="text-gray-600">IVA 21% e IRPF 15% por trimestre</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <CalendarIcon className="h-5 w-5 text-gray-400" />
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          >
            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Resumen Anual */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-blue-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-blue-600">
            {formatCurrency(totalYearRevenue)}
          </div>
          <div className="text-sm text-blue-700">Ingresos Totales</div>
        </div>
        <div className="bg-green-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-600">
            {formatCurrency(totalYearIVA)}
          </div>
          <div className="text-sm text-green-700">IVA Total (21%)</div>
        </div>
        <div className="bg-red-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-red-600">
            {formatCurrency(totalYearIRPF)}
          </div>
          <div className="text-sm text-red-700">IRPF Total (15%)</div>
        </div>
        <div className="bg-purple-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-purple-600">
            {totalYearInvoices}
          </div>
          <div className="text-sm text-purple-700">Facturas</div>
        </div>
      </div>

      {/* Gráfico de Barras */}
      <div className="mb-8">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Ingresos por Trimestre</h4>
        <div className="grid grid-cols-4 gap-4 h-48 items-end">
          {mergedQuarters.map((quarter, index) => (
            <div key={quarter.quarter} className="text-center">
              <div className="relative h-full flex flex-col justify-end">
                {/* Barra de ingresos */}
                <div 
                  className="bg-blue-500 rounded-t-lg transition-all duration-300 hover:bg-blue-600"
                  style={{ height: `${getBarHeight(quarter.totalRevenue, maxRevenue)}%` }}
                >
                  <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-xs font-medium text-gray-700">
                    {formatCurrency(quarter.totalRevenue)}
                  </div>
                </div>
              </div>
              <div className="mt-2 text-sm font-medium text-gray-900">
                {quarter.quarter}
              </div>
              <div className="text-xs text-gray-600">
                {getQuarterName(quarter.quarter)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Detalle por Trimestre */}
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-gray-900">Desglose Trimestral</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {mergedQuarters.map((quarter) => (
            <div key={quarter.quarter} className="bg-gray-50 rounded-lg p-4">
              <div className="text-center mb-3">
                <div className="text-lg font-bold text-gray-900">{quarter.quarter}</div>
                <div className="text-sm text-gray-600">{getQuarterName(quarter.quarter)}</div>
              </div>
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Ingresos:</span>
                  <span className="font-medium">{formatCurrency(quarter.totalRevenue)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">IVA (21%):</span>
                  <span className="font-medium text-green-600">{formatCurrency(quarter.totalIVA)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">IRPF (15%):</span>
                  <span className="font-medium text-red-600">{formatCurrency(quarter.totalIRPF)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Facturas:</span>
                  <span className="font-medium">{quarter.invoiceCount}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Gráfico de Tendencias */}
      <div className="mt-8">
        <h4 className="text-lg font-semibold text-gray-900 mb-4">Tendencia Anual</h4>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-center space-x-8">
            <div className="text-center">
              <ArrowTrendingUpIcon className="h-8 w-8 text-green-600 mx-auto mb-2" />
              <div className="text-sm text-gray-600">IVA a Pagar</div>
              <div className="text-lg font-bold text-green-600">{formatCurrency(totalYearIVA)}</div>
            </div>
            <div className="text-center">
              <ArrowTrendingDownIcon className="h-8 w-8 text-red-600 mx-auto mb-2" />
              <div className="text-sm text-gray-600">IRPF a Retener</div>
              <div className="text-lg font-bold text-red-600">{formatCurrency(totalYearIRPF)}</div>
            </div>
            <div className="text-center">
              <CurrencyEuroIcon className="h-8 w-8 text-blue-600 mx-auto mb-2" />
              <div className="text-sm text-gray-600">Neto</div>
              <div className="text-lg font-bold text-blue-600">
                {formatCurrency(totalYearRevenue - totalYearIVA + totalYearIRPF)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 